(() => {
  const dimensionsByProduct = new Map();
  const dimensionsPattern = /\s*\((\d+(?:\.\d+)?)x(\d+(?:\.\d+)?)x(\d+(?:\.\d+)?)cm\)$/i;

  class SofaworxBaseVariants extends HTMLElement {
    connectedCallback() {
      this.prepare();
    }

    static filter(variants, dimensions) {
      const hasSizes = variants.some(variant => dimensionsPattern.test(variant.title));
      if (hasSizes && !['length_cm', 'width_cm', 'height_cm'].every(key => Number(dimensions?.[key]) > 0)) {
        throw new Error('Base dimensions are unavailable');
      }
      const finishes = new Map();
      for (const variant of variants) {
        const size = variant.title.match(dimensionsPattern);
        if (hasSizes && (!size || !['length_cm', 'width_cm', 'height_cm'].every((key, index) =>
          Math.abs(Number(size[index + 1]) - Number(dimensions[key])) < 0.05
        ))) continue;
        const finish = variant.title.replace(dimensionsPattern, '').trim().toLowerCase();
        const existing = finishes.get(finish);
        if (!existing || (!existing.available && variant.available)) finishes.set(finish, variant);
      }
      return [...finishes.values()];
    }

    static async prepareRoot(root) {
      // Detached section-rendering HTML has not upgraded its custom elements yet.
      customElements.upgrade(root);
      await Promise.all([...root.querySelectorAll('sofaworx-base-variants')].map(el => el.prepare()));
    }

    prepare() {
      if (this.ready) return this.ready;
      this.ready = this.load();
      return this.ready;
    }

    async load() {
      const card = this.closest('[data-product-card], [data-range-card]');
      if (!card) return;
      const swatches = card.querySelector('sofaworx-card-swatches, sofaworx-range-swatches');
      const randomData = card.querySelector('[data-carousel-variants]');
      if (swatches) swatches.hidden = true;
      try {
        const variants = JSON.parse(this.querySelector('script').textContent);
        let dimensions;
        if (variants.some(variant => dimensionsPattern.test(variant.title))) {
          const id = this.dataset.productId;
          if (!dimensionsByProduct.has(id)) {
            // The variant generator uses the first fully dimensioned BOM
            // component, which can differ from the overall size of a corner unit.
            dimensionsByProduct.set(id, fetch(`https://api.mira.furniture/bom/components?shopifyProductId=${encodeURIComponent(id)}`, {
              signal: AbortSignal.timeout(8000),
            }).then(response => {
              if (!response.ok) throw new Error('Mira dimensions could not be loaded');
              return response.json();
            }).then(data => data.components?.find(component => Number(component.component_id) > 0 &&
              ['length_cm', 'width_cm', 'height_cm'].every(key => Number(component[key]) > 0)
            )).catch(error => {
              dimensionsByProduct.delete(id);
              throw error;
            }));
          }
          dimensions = await dimensionsByProduct.get(id);
        }
        const base = SofaworxBaseVariants.filter(variants, dimensions);
        if (!base.length) throw new Error('No standard-size finishes');
        if (randomData) randomData.textContent = JSON.stringify(base.filter(v => v.available && v.image));
        const selected = base.find(v => v.available && v.image) || base.find(v => v.available) || base[0];
        const image = card.querySelector('[data-card-img]');
        if (image && selected.image) {
          image.src = selected.image;
          image.removeAttribute('srcset');
        }
        card.querySelectorAll('[data-card-link]').forEach(link => { link.href = selected.url; });
        const label = card.querySelector('[data-carousel-fabric]');
        if (label) label.textContent = selected.title;
        if (swatches) this.rebuildSwatches(swatches, base, selected);
        card.dataset.baseVariantCount = String(base.length);
        card.dataset.baseVariantsReady = 'true';
      } catch {
        // Keep size-unverified options out of the UI and random draw. The
        // product remains accessible through its normal product card link.
        if (randomData) randomData.textContent = '[]';
        card.dataset.baseVariantsReady = 'unavailable';
      }
    }

    rebuildSwatches(group, variants, selected) {
      const template = group.querySelector('[data-swatch]');
      if (!template) return;
      const max = Number(this.dataset.maxSwatches) || 6;
      const visible = variants.slice(0, max);
      if (!visible.includes(selected)) visible[visible.length - 1] = selected;
      const nodes = visible.map(variant => {
        const button = template.cloneNode(true);
        button.title = variant.title;
        button.setAttribute('aria-label', variant.title);
        button.setAttribute('aria-pressed', String(variant.url === selected.url));
        button.dataset.variantUrl = variant.url;
        delete button.dataset.srcset;
        delete button.dataset.image;
        if (variant.image) {
          button.dataset.image = variant.image;
          const thumbnail = button.querySelector('img') || document.createElement('img');
          thumbnail.className = template.querySelector('img')?.className || '';
          const url = new URL(variant.image, location.origin);
          url.searchParams.set('width', '96');
          thumbnail.src = url.href;
          thumbnail.removeAttribute('srcset');
          thumbnail.alt = variant.title;
          button.replaceChildren(thumbnail);
        } else {
          button.replaceChildren();
        }
        return button;
      });
      const more = group.querySelector('a');
      if (more && variants.length > max) {
        more.textContent = `+${variants.length - visible.length}`;
        more.setAttribute('aria-label', `View all ${variants.length} fabric options`);
        nodes.push(more);
      }
      group.replaceChildren(...nodes);
      const images = new Set(variants.map(v => v.image).filter(Boolean));
      group.hidden = images.size < 2;
    }
  }

  if (!customElements.get('sofaworx-base-variants')) customElements.define('sofaworx-base-variants', SofaworxBaseVariants);
})();
